import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { addDays, format } from "date-fns";
import { DateTime } from "luxon";
import { Calendar as CalendarIcon, Check, CircleUser } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Calendar } from "@/components/ui/calendar";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { endOfWeek } from "@/lib/dates/weekStart";
import { useWeekStartsOn } from "@/lib/dates/useWeekStartsOn";
import { useOrg } from "@/lib/states/session/hooks";
import { trpc } from "@/lib/trpc/client";
import { cn } from "@/lib/utils/ui";
import type { TaskRow } from "./TaskList";

export const displayName = (user?: {
  first_name?: string | null;
  last_name?: string | null;
  email?: string | null;
} | null) => {
  if (!user) return "Unassigned";
  const name = `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim();
  return name || user.email || "Unassigned";
};

export const initialsFor = (first?: string | null, last?: string | null) =>
  `${first?.[0] ?? ""}${last?.[0] ?? ""}`.trim().toUpperCase() || "?";

export const clientName = (client?: {
  first_name?: string | null;
  last_name?: string | null;
} | null) =>
  `${client?.first_name ?? ""} ${client?.last_name ?? ""}`.trim() || "Client";

const dueDateFromIso = (dueAt: string | null | undefined) => {
  if (!dueAt) return null;
  const date = DateTime.fromISO(dueAt.slice(0, 10), { zone: "utc" }).startOf(
    "day",
  );
  return date.isValid ? date : null;
};

export function formatDueLabel(dueAt: string | null | undefined) {
  const date = dueDateFromIso(dueAt);
  if (!date) return null;
  const relative = date.toRelativeCalendar();
  if (!relative) return date.toFormat("MMM d");
  return relative.charAt(0).toUpperCase() + relative.slice(1);
}

export function formatDueTooltip(dueAt: string | null | undefined) {
  const date = dueDateFromIso(dueAt);
  if (!date) return null;
  return `Due ${date.toFormat("d MMM yyyy")}`;
}

export function isDueOnOrBeforeToday(dueAt: string | null | undefined) {
  const date = dueDateFromIso(dueAt);
  if (!date) return false;
  return date.toFormat("yyyy-MM-dd") <= toIsoDate(new Date());
}

export const toIsoDate = (date: Date) => {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const toDueAt = (isoDate: string | null) =>
  isoDate ? `${isoDate}T00:00:00.000Z` : null;

const getMenuHost = (trigger: HTMLElement | null) =>
  trigger?.closest<HTMLElement>("[role='dialog']") ?? document.body;

export function BadgeMenu({
  open,
  onOpenChange,
  trigger,
  children,
  className,
  side = "top",
  align = "start",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: ReactNode;
  children: ReactNode;
  className?: string;
  side?: "top" | "bottom";
  align?: "start" | "end";
}) {
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [host, setHost] = useState<HTMLElement | null>(null);
  const [coords, setCoords] = useState<{
    top?: number;
    bottom?: number;
    left?: number;
    right?: number;
    position: "fixed" | "absolute";
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setHost(null);
      setCoords(null);
      return;
    }

    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      const nextHost = getMenuHost(trigger);
      setHost(nextHost);
      const inDialog = nextHost !== document.body;
      const triggerRect = trigger.getBoundingClientRect();
      const hostRect = inDialog
        ? nextHost.getBoundingClientRect()
        : { top: 0, left: 0, width: window.innerWidth, height: window.innerHeight };
      const menuWidth = menuRef.current?.offsetWidth || (align === "end" ? 144 : 256);
      const maxLeft = Math.max(8, hostRect.width - menuWidth - 8);
      const left =
        align === "end"
          ? Math.min(
              Math.max(8, triggerRect.right - hostRect.left - menuWidth),
              maxLeft,
            )
          : Math.min(Math.max(8, triggerRect.left - hostRect.left), maxLeft);
      const vertical =
        side === "bottom"
          ? { top: triggerRect.bottom - hostRect.top + 4 }
          : {
              bottom: hostRect.height - (triggerRect.top - hostRect.top) + 4,
            };
      setCoords({
        position: inDialog ? "absolute" : "fixed",
        ...vertical,
        left,
      });
    };

    updatePosition();
    const frame = window.requestAnimationFrame(() => {
      updatePosition();
      menuRef.current
        ?.querySelector<HTMLInputElement>("input:not([type='hidden'])")
        ?.focus({ preventScroll: true });
    });
    window.addEventListener("resize", updatePosition);
    document.addEventListener("scroll", updatePosition, true);
    return () => {
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updatePosition);
      document.removeEventListener("scroll", updatePosition, true);
    };
  }, [align, open, side]);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      if (!(event.target instanceof Node)) return;
      if (triggerRef.current?.contains(event.target)) return;
      if (menuRef.current?.contains(event.target)) return;
      onOpenChange(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open, onOpenChange]);

  return (
    <div ref={triggerRef} className="inline-flex w-fit max-w-full">
      {trigger}
      {open && coords && host
        ? createPortal(
            <div
              ref={menuRef}
              className={cn(
                "z-[80] overflow-hidden rounded-md border bg-popover text-popover-foreground shadow-md",
                className,
              )}
              style={{
                position: coords.position,
                top: coords.top,
                bottom: coords.bottom,
                left: coords.left,
                right: coords.right,
              }}
            >
              {children}
            </div>,
            host,
          )
        : null}
    </div>
  );
}

function PersonTrigger({
  name,
  avatarUrl,
  initials,
  disabled,
  empty,
  onClick,
}: {
  name: string;
  avatarUrl?: string | null;
  initials: string;
  disabled?: boolean;
  empty?: boolean;
  onClick?: () => void;
}) {
  const content = (
    <>
      {empty ? (
        <CircleUser className="h-4 w-4 shrink-0" />
      ) : (
        <Avatar className="h-5 w-5">
          <AvatarImage src={avatarUrl ?? undefined} className="object-cover" />
          <AvatarFallback className="text-[9px]">{initials}</AvatarFallback>
        </Avatar>
      )}
      <span className="truncate">{name}</span>
    </>
  );

  if (disabled) {
    return (
      <span
        className={cn(
          "inline-flex max-w-full items-center gap-1.5 text-xs",
          empty ? "text-muted-foreground" : "text-foreground",
        )}
      >
        {content}
      </span>
    );
  }

  return (
    <button
      type="button"
      role="combobox"
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 rounded-md text-xs hover:text-foreground",
        empty ? "text-muted-foreground" : "text-foreground",
      )}
      onClick={onClick}
    >
      {content}
    </button>
  );
}

export function AssigneeMenu({
  assignedTo,
  assignee,
  disabled,
  onSelect,
}: {
  assignedTo: string | null;
  assignee: TaskRow["assignee"];
  disabled?: boolean;
  onSelect: (userId: string | null) => void;
}) {
  const org = useOrg();
  const [open, setOpen] = useState(false);
  const { data: assignees } = trpc.tasks.listAssignees.useQuery(undefined, {
    enabled: open && Boolean(org?.id),
  });

  return (
    <BadgeMenu
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      className="w-64"
      trigger={
        <PersonTrigger
          name={displayName(assignee)}
          avatarUrl={assignee?.avatar?.url}
          initials={initialsFor(assignee?.first_name, assignee?.last_name)}
          disabled={disabled}
          empty={!assignee}
          onClick={() => setOpen((current) => !current)}
        />
      }
    >
      <Command loop>
        <CommandInput autoFocus placeholder="Assign to..." />
        <CommandList>
          <CommandEmpty>No coaches found.</CommandEmpty>
          <CommandGroup>
            <CommandItem
              value="unassigned"
              className="gap-2"
              onSelect={() => {
                onSelect(null);
                setOpen(false);
              }}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
                <CircleUser className="h-5 w-5" />
              </span>
              <span className="flex-1">Unassigned</span>
              <Check
                className={cn("h-4 w-4", assignedTo ? "opacity-0" : "opacity-100")}
              />
            </CommandItem>
            {(assignees ?? []).map((coach) => (
              <CommandItem
                key={coach.user_id}
                value={`${displayName(coach)} ${coach.email ?? ""}`}
                className="gap-2"
                onSelect={() => {
                  onSelect(coach.user_id);
                  setOpen(false);
                }}
              >
                <Avatar className="h-5 w-5 border">
                  <AvatarImage
                    src={coach.avatar?.url ?? undefined}
                    className="object-cover"
                  />
                  <AvatarFallback className="text-[9px]">
                    {initialsFor(coach.first_name, coach.last_name)}
                  </AvatarFallback>
                </Avatar>
                <span className="flex-1 truncate">{displayName(coach)}</span>
                <Check
                  className={cn(
                    "h-4 w-4",
                    assignedTo === coach.user_id ? "opacity-100" : "opacity-0",
                  )}
                />
              </CommandItem>
            ))}
          </CommandGroup>
        </CommandList>
      </Command>
    </BadgeMenu>
  );
}

export function ClientMenu({
  client,
  disabled,
  onSelect,
}: {
  client?: TaskRow["clients"][number] | null;
  disabled?: boolean;
  onSelect: (clientId: string | null) => void;
}) {
  const org = useOrg();
  const [open, setOpen] = useState(false);
  const { data: clients } = trpc.clients.list.useQuery(undefined, {
    enabled: open && Boolean(org?.id),
  });
  const clientOptions = useMemo(
    () =>
      (clients ?? []).filter(
        (row) => row.status !== "ARCHIVED" && !row.archived_at,
      ),
    [clients],
  );

  return (
    <BadgeMenu
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      className="w-64"
      trigger={
        <PersonTrigger
          name={client ? clientName(client) : "No client"}
          avatarUrl={client?.avatar?.url}
          initials={initialsFor(client?.first_name, client?.last_name)}
          disabled={disabled}
          empty={!client}
          onClick={() => setOpen((current) => !current)}
        />
      }
    >
      <Command loop>
        <CommandInput autoFocus placeholder="Link a client..." />
        <CommandList>
          <CommandEmpty>No clients found.</CommandEmpty>
          <CommandGroup>
            <CommandItem
              value="no-client"
              className="gap-2"
              onSelect={() => {
                onSelect(null);
                setOpen(false);
              }}
            >
              <span className="flex h-5 w-5 shrink-0 items-center justify-center text-muted-foreground">
                <CircleUser className="h-5 w-5" />
              </span>
              <span className="flex-1">No client</span>
              <Check
                className={cn("h-4 w-4", client ? "opacity-0" : "opacity-100")}
              />
            </CommandItem>
            {clientOptions.map((row) => {
              const name = clientName(row);
              return (
                <CommandItem
                  key={row.id}
                  value={`${name} ${row.email ?? ""} ${row.id}`}
                  className="gap-2"
                  onSelect={() => {
                    onSelect(row.id);
                    setOpen(false);
                  }}
                >
                  <Avatar className="h-5 w-5 border">
                    <AvatarImage
                      src={row.avatar?.url ?? undefined}
                      className="object-cover"
                    />
                    <AvatarFallback className="text-[9px]">
                      {initialsFor(row.first_name, row.last_name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="flex-1 truncate">{name}</span>
                  <Check
                    className={cn(
                      "h-4 w-4",
                      client?.id === row.id ? "opacity-100" : "opacity-0",
                    )}
                  />
                </CommandItem>
              );
            })}
          </CommandGroup>
        </CommandList>
      </Command>
    </BadgeMenu>
  );
}

export function DueDateMenu({
  dueAt,
  disabled,
  onSelect,
}: {
  dueAt: string | null;
  disabled?: boolean;
  onSelect: (dueAt: string | null) => void;
}) {
  const weekStartsOn = useWeekStartsOn();
  const [open, setOpen] = useState(false);
  const dueLabel = formatDueLabel(dueAt);
  const dueDate = dueAt?.slice(0, 10) ?? "";
  const duePresets = useMemo(() => {
    const tomorrow = addDays(new Date(), 1);
    const weekEnd = endOfWeek(new Date(), weekStartsOn);
    const inOneWeek = addDays(new Date(), 7);
    return [
      { label: "Tomorrow", date: tomorrow },
      { label: "End of this week", date: weekEnd },
      { label: "In one week", date: inOneWeek },
    ];
  }, [weekStartsOn]);

  const label = dueLabel ? `Due ${dueLabel.toLowerCase()}` : "No due date";
  const fullDateLabel = formatDueTooltip(dueAt);
  const content = (
    <>
      <CalendarIcon className="h-4 w-4 shrink-0 text-primary" />
      <span className="truncate">{label}</span>
    </>
  );

  const trigger = disabled ? (
    <span className="inline-flex max-w-full items-center gap-2 text-xs">
      {content}
    </span>
  ) : (
    <button
      type="button"
      className="inline-flex max-w-full items-center gap-2 rounded-md text-xs hover:text-foreground"
      onClick={() => setOpen((current) => !current)}
    >
      {content}
    </button>
  );

  return (
    <BadgeMenu
      open={open}
      onOpenChange={setOpen}
      side="bottom"
      className="w-64"
      trigger={
        fullDateLabel ? (
          <TooltipProvider delayDuration={200}>
            <Tooltip open={open ? false : undefined}>
              <TooltipTrigger asChild>{trigger}</TooltipTrigger>
              <TooltipContent side="top" className="z-[80]">
                {fullDateLabel}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        ) : (
          trigger
        )
      }
    >
      <div className="border-b p-1">
        {dueDate ? (
          <button
            type="button"
            className="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            onClick={() => {
              onSelect(null);
              setOpen(false);
            }}
          >
            No due date
          </button>
        ) : null}
        {duePresets.map((preset) => (
          <button
            key={preset.label}
            type="button"
            className="flex w-full items-center justify-between rounded-sm px-2 py-1.5 text-sm hover:bg-accent"
            onClick={() => {
              onSelect(toDueAt(toIsoDate(preset.date)));
              setOpen(false);
            }}
          >
            <span>{preset.label}</span>
            <span className="text-muted-foreground">
              {format(preset.date, "EEE, d MMM")}
            </span>
          </button>
        ))}
      </div>
      <Calendar
        mode="single"
        weekStartsOn={weekStartsOn}
        className="w-full"
        classNames={{ root: "w-full" }}
        selected={dueDate ? new Date(`${dueDate}T00:00:00`) : undefined}
        onSelect={(date) => {
          onSelect(date ? toDueAt(toIsoDate(date)) : null);
          setOpen(false);
        }}
      />
    </BadgeMenu>
  );
}
